import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { Constants } from 'utilities/constants';
import { getStage } from 'components/underwrite/postAcquisition/import/importTasks/taxonomyHelpers';

export const initialState = {
  [Constants.Module.EXPLORE]: {
    importSelectedTab: null,
    [Constants.ImportType.PROPERTIES]: {
      importName: "",
      importNameValidation: { valid: null, message: "" },
      importRows: [],
      gridEditDirty: false,
      importRowsSkippedCount: null,
      importGeoValidationResult: { passedValidation: null, invalidZipCodes: null, invalidCounties: null, invalidGeo: null },
      importDupCheckErrors: []
    },
    importStatuses: {},
    importLoadingOverlayState: {
      isLoading: false,
      message: null
    }
  },
  [Constants.Module.UNDERWRITE]: {
    importSelectedTab: null,
    [Constants.ImportType.OPPORTUNITIES]: {
      importSelectedChannel: '',
      importOpportunityStatus: Constants.OpportunityStatus.INITIAL,
      importName: "",
      importNameValidation: { valid: null, message: "" },
      importRows: [],
      isFromRejectedImports: false,
      gridEditDirty: false,
      importRowsSkippedCount: null,
      importGeoValidationResult: { passedValidation: null, invalidZipCodes: null, invalidCounties: null, invalidGeo: null },
      importDupCheckErrors: []
    },
    [Constants.ImportType.TITLES]: {
      importRows: [],
      gridEditDirty: false,
      importRowsSkippedCount: null,
    },
    importStatuses: {},
    importLoadingOverlayState: {
      isLoading: false,
      message: null
    }
  },
  [Constants.Module.POSTACQ]: {
    importSelectedTab: null,
    [Constants.ImportType.POSTACQTASKS]: {
      channel: '',
      requestType: '',
      importName: null,
      importNameValidation: { valid: null, message: "" },
      importRows: [],
      gridEditDirty: false,
      importRowsSkippedCount: null,
      importDupCheckErrors: []
    },
    importStatuses: {},
    importLoadingOverlayState: {
      isLoading: false,
      message: null
    }
  },
  [Constants.Module.ADMIN]: {
    importSelectedTab: null,
    [Constants.ImportType.BACKFILL]: {
      importRows: [],
    },
    importLoadingOverlayState: {
      isLoading: false,
      message: null
    }
  }
}

const getModuleFromImportType = importType => {
  switch (importType) {
    case Constants.ImportType.PROPERTIES:
      return Constants.Module.EXPLORE;
    case Constants.ImportType.OPPORTUNITIES:
    case Constants.ImportType.TITLES:
      return Constants.Module.UNDERWRITE;
    case Constants.ImportType.BACKFILL:
      return Constants.Module.ADMIN;
    case Constants.ImportType.POSTACQTASKS:
      return Constants.Module.POSTACQ;
    default:
      console.log('Could not find module for import type ' + importType);
      return null;
  }
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'importSlice',
  initialState,
  reducers: {
    setImportOpportunityStatus: (state, action) => {
      const { importType, importOpportunityStatus } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importOpportunityStatus = importOpportunityStatus;
    },
    setImportName: (state, action) => {
      const { importType, importName } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importName = importName;
    },
    setImportNameValidation: (state, action) => {
      const { importType, importNameValidation } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importNameValidation = {
        ...state[module][importType].importNameValidation,
        ...importNameValidation
      }
    },
    setImportSelectedTab: (state, action) => {
      const { moduleName, importSelectedTab } = action.payload;
      state[moduleName].importSelectedTab = importSelectedTab;
    },
    setOpportunityImportSelectedChannel: (state, action) => {
      state[Constants.Module.UNDERWRITE][Constants.ImportType.OPPORTUNITIES].importSelectedChannel = action.payload;
    },
    setPostAcqTaskImportChannel: (state, action) => {
      // Changing channel invalidates any previously-picked request type
      state[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].channel = action.payload;
      state[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].requestType = '';
    },
    setPostAcqTaskImportRequestType: (state, action) => {
      state[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].requestType = action.payload
    },
    setImportRows: (state, action) => {
      const { importType, importRows } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importRows = importRows;
    },
    setIsFromRejectedImports: (state, action) => {
      const { importType, isFromRejectedImports } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].isFromRejectedImports = isFromRejectedImports;
    },
    setImportGridEditDirty: (state, action) => {
      const { importType, gridEditDirty } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].gridEditDirty = gridEditDirty;
    },
    setImportGeoValidationResult: (state, action) => {
      const { importType, importGeoValidationResult } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importGeoValidationResult = importGeoValidationResult;
    },
    setImportDupCheckErrors: (state, action) => {
      const { importType, dupCheckErrors } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importDupCheckErrors = dupCheckErrors;
    },
    setImportRowsSkippedCount: (state, action) => {
      const { importType, importRowsSkippedCount } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module][importType].importRowsSkippedCount = importRowsSkippedCount;
    },
    setImportHistoryRefresh: (state, action) => {
      const { module, value } = action.payload;
      state[module].importHistoryRefresh = value;
    },
    setImportStatuses: (state, action) => {
      const { module, importStatuses } = action.payload;
      state[module].importStatuses = importStatuses;
    },
    setImportLoadingOverlayState: (state, action) => {
      const { importType, importLoadingOverlayState } = action.payload;
      const module = getModuleFromImportType(importType);
      state[module].importLoadingOverlayState = importLoadingOverlayState;
    },
    // Redux saga actions, no reducers
    revalidateImportRows: () => { }, // payload = { importType, rows, isFromRejectedImports }
    validateGeoOnServer: () => { }, // payload = { importType }
    autoFillDescriptives: () => { }, // payload = { importType, fieldType }
    submitImport: () => { }, // payload = { importType, onSubmitImportSuccess }
    setImportRawData: () => { }, // payload = { data, importType, source }
    fetchRejectedImportsOpportunities: () => { }, // payload = { id, callback }
    addStatusToImportStatuses: () => { } // payload = backend statusDto through signalR
  }
});

export default slice.reducer;

export const importActions = slice.actions;

// Memoized selectors
export const importSelectors = {
  importStateSelector: createSelector(
    state => state.importState,
    importState => importState
  ),
  importModuleStateSelector: createSelector( // Pass in module name as arg
    (state, module) => {
      return state.importState[module];
    },
    importModuleState => importModuleState
  ),
  importTypeStateSelector: createSelector( // Pass in importType name as arg
    (state, importType) => {
      const module = getModuleFromImportType(importType);
      return state.importState[module]?.[importType];
    },
    importTypeState => importTypeState
  ),
  opportunityImportSelectedChannelSelector: createSelector(
    state => state.importState[Constants.Module.UNDERWRITE][Constants.ImportType.OPPORTUNITIES].importSelectedChannel,
    importSelectedChannel => importSelectedChannel
  ),
  canImportSelector: createSelector(
    (state, importType = Constants.ImportType.OPPORTUNITIES) => {
      const importTypeState = importSelectors.importTypeStateSelector(state, importType);
      const { importRows, importGeoValidationResult, importSelectedChannel } = importTypeState;
  
      let canImport = true;
  
      if (importType === Constants.ImportType.OPPORTUNITIES && importSelectedChannel === '') {
        canImport = false;
      }
      if (!importRows?.length || importRows.some(r => r.invalidColumns?.length > 0)) {
        canImport = false;
      }
  
      if (importGeoValidationResult?.invalidGeo?.length) {
        canImport = false;
      }
      return canImport;
    },
    canImport => canImport
  ),
  postAcqTaskImportChannelSelector: createSelector(
    state => state.importState[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].channel,
    channel => channel
  ),
  postAcqTaskImportRequestTypeSelector: createSelector(
    state => state.importState[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].requestType,
    requestType => requestType
  ),
  postAcqTaskImportStageSelector: createSelector(
    state => state.importState[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].channel,
    state => state.importState[Constants.Module.POSTACQ][Constants.ImportType.POSTACQTASKS].requestType,
    state => state.appState.appSettings.postAcquisitionTaxonomy?.entries ?? [],
    (channel, requestType, entries) =>
      (channel && requestType) ? getStage(entries, channel, requestType) : null
  )
}
